import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom'
import { useApis } from '../../api/useApiClient';
import { useNotification } from '../../context/NotificationContext';

import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';

import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';

import Form from '../Common/Form';
import ItemPicker from '../Common/Form/ItemPicker';
import LoadingDetailsView from '../Common/Skeletons/LoadingDetailsView';

import CustomersList from '../Customer/CustomersList';

const VehicleForm = ({ editMode = false }) => {

    const { tenantApi } = useApis();
    const navigate = useNavigate();
    const { showNotification } = useNotification();

    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(false);
    const regNumRef = useRef(null);

    const QUICK_INSERT_CHARS = ['යුහ', 'නාහ', 'ගුවන්', 'ශ්‍රී'];

    // Load vehicle data if in edit mode
    const { id } = useParams(); // Get the vehicle ID from the URL parameters if provided (in edit mode)
    const loadformData = async () => {
        setLoading(true);
        try {
            const res = await tenantApi(`/tenants/vehicles/${id}`);
            // Explicitly set each field to make sure only modifiable fields are set in the update request
            setFormData({
                registrationNumber: res.data?.registrationNumber || '',
                ownerId: res.data?.currentOwner?.id || '',
                make: res.data?.make || '',
                model: res.data?.model || '',
                year: res.data?.year || '',
                fuelType: res.data?.fuelType || '',
                color: res.data?.color || '',
                chassisNo: res.data?.chassisNo || '',
                engineNo: res.data?.engineNo || '',
                additionalInfo: res.data?.additionalInfo || '',
            });
            console.debug("Vehicle data loaded successfully:", res.data);
        } catch (error) {
            console.error("Error loading vehicle data", error);
            showNotification(error.message || "Failed to load vehicle details", 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (editMode) {
            loadformData();
        }
    }, [editMode]);

    // --- end of loading vehicle data for editing



    // --- Handle input changes
    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    // Insert a special character at the current cursor position in the registration number field
    const handleInsertChar = (char) => {
        const input = regNumRef.current;
        const currentValue = formData.registrationNumber || '';
        const cursorPos = input?.selectionStart ?? currentValue.length;
        const newValue = currentValue.slice(0, cursorPos) + char + currentValue.slice(cursorPos);
        const newCursorPos = cursorPos + char.length;

        setFormData({ ...formData, registrationNumber: newValue });

        // Restore focus and cursor position after React re-render
        setTimeout(() => {
            if (input) {
                input.focus();
                input.setSelectionRange(newCursorPos, newCursorPos);
            }
        }, 0);
    };


    // Show skeliton while loading initial data (if in edit mode)
    if (loading)
        return <LoadingDetailsView />


    return (
        <Box sx={{ padding: { xs: 2, sm: 3 }, maxWidth: '800px' }}>
            <Form
                resourceName='Vehicle'
                apiPath='/tenants/vehicles'
                formData={formData}
                editMode={editMode}
                onSubmit={() => {
                    navigate(-1);
                }}
                onCancel={() => {
                    navigate(-1);
                }}
            >
                <Stack spacing={3}>
                    {/* Name Group */}
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <Box sx={{ flex: 1, position: 'relative', minWidth: '350px'}}>
                            <TextField
                                label="Registration number"
                                name="registrationNumber"
                                value={formData.registrationNumber || ''}
                                onChange={handleChange}
                                inputRef={regNumRef}
                                required
                                fullWidth
                            />
                            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                                {QUICK_INSERT_CHARS.map((char) => (
                                    <Chip
                                        key={char}
                                        label={char}
                                        size="small"
                                        variant="outlined"
                                        onClick={() => handleInsertChar(char)}
                                        sx={{ cursor: 'pointer' }}
                                    />
                                ))}
                            </Stack>
                        </Box>

                        <ItemPicker
                            label="Owner"
                            name="ownerId"
                            initialValue={{
                                id: formData.currentOwner?.id || '',
                                name: formData.currentOwner?.name || ''
                            }}
                            ListView={CustomersList}
                            onChange={(event) => setFormData({ ...formData, ownerId: event.target.value })}
                            required
                            fullWidth
                        />
                    </Stack>

                    {/* Contact Details Group */}
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>

                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField
                            label="Make"
                            name="make"
                            value={formData.make || ''}
                            onChange={handleChange}
                            fullWidth
                        />
                        <TextField
                            label="Model"
                            name="model"
                            value={formData.model || ''}
                            onChange={handleChange}
                            type="text"
                            fullWidth
                        />

                        <TextField
                            label="Year"
                            name="year"
                            value={formData.year || ''}
                            onChange={handleChange}
                            type="number"
                            fullWidth

                            // for inputProps with lowercase i is for the native input element
                            inputProps={{
                                onWheel: (e) => e.currentTarget.blur(), // Prevent changing number input on wheel/scroll    
                            }}
                        />
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <FormControl fullWidth>
                            <InputLabel id="vehicle-type-input-label">Vehicle type</InputLabel>
                            <Select
                                labelId="vehicle-type-input-label"
                                id="vehicle-type-select"
                                name="type"
                                value={formData.type || ''}
                                label="Vehicle type"
                                onChange={handleChange}
                            >
                                <MenuItem value="Car">Car</MenuItem>
                                <MenuItem value="Van">Van</MenuItem>
                                <MenuItem value="SUV">SUV</MenuItem>
                                <MenuItem value="Cab">Cab</MenuItem>
                                <MenuItem value="Truck">Truck</MenuItem>
                                <MenuItem value="Lorry">Lorry</MenuItem>
                                <MenuItem value="Motorcycle">Motorcycle</MenuItem>
                                <MenuItem value="Other">Other</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel id="transmission-input-label">Transmission</InputLabel>
                            <Select
                                labelId="transmission-input-label"
                                id="transmission-select"
                                name="transmission"
                                value={formData.transmission || ''}
                                label="Transmission"
                                onChange={handleChange}
                            >
                                <MenuItem value="Automatic">Automatic</MenuItem>
                                <MenuItem value="Manual">Manual</MenuItem>
                                <MenuItem value="Semi-Automatic">Semi-Automatic</MenuItem>
                                <MenuItem value="CVT">CVT</MenuItem>
                                <MenuItem value="Other">Other</MenuItem>
                            </Select>
                        </FormControl>
                        
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <FormControl fullWidth>
                            <InputLabel id="fuel-type-input-label">Fuel type</InputLabel>
                            <Select
                                labelId="fuel-type-input-label"
                                id="fuel-type-select"
                                name="fuelType"
                                value={formData.fuelType || ''}
                                label="Fuel type"
                                onChange={handleChange}
                            >
                                <MenuItem value="Petrol">Petrol</MenuItem>
                                <MenuItem value="Diesel">Diesel</MenuItem>
                                <MenuItem value="Electric">Electric</MenuItem>
                                <MenuItem value="Hybrid">Hybrid</MenuItem>
                                <MenuItem value="Other">Other</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label="Color"
                            name="color"
                            value={formData.color || ''}
                            onChange={handleChange}
                            fullWidth
                        />
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField
                            label="Chassis number"
                            name="chassisNo"
                            value={formData.chassisNo || ''}
                            onChange={handleChange}
                            fullWidth
                        />
                        <TextField
                            label="Engine number"
                            name="engineNo"
                            value={formData.engineNo || ''}
                            onChange={handleChange}
                            fullWidth
                        />
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField
                            label="Additional Info"
                            name="additionalInfo"
                            value={formData.additionalInfo}
                            onChange={handleChange}
                            multiline
                            rows={4}
                            fullWidth
                        />
                    </Stack>
                </Stack>
            </Form>
        </Box >
    );
};

export default VehicleForm;